import { useId } from 'react';

import Button from '@/components/ui/button';
import { FieldError, inputCls } from '@/components/ui/field';
import type { LineRow } from '@/domain/events';
import type { FieldBinder } from '@/lib/validation';

/**
 * A short editable list of two-column rows — the run of the evening, or the
 * good-to-know notes. Both are the same shape, so they share an editor.
 *
 * Rows are addressed by index rather than by key: the first column is free
 * text the operator is still typing, so it is not stable enough to key on.
 */
export default function LineEditor({
  label,
  hint,
  columns,
  firstType = 'text',
  firstWidth = 'w-[140px]',
  value,
  onChange,
  path,
  field,
}: {
  label: string;
  hint?: string;
  /** Placeholders for the two columns. */
  columns: [string, string];
  firstType?: 'text' | 'time';
  firstWidth?: string;
  value: LineRow[];
  onChange: (next: LineRow[]) => void;
  /** Where the list sits in the draft; a cell's error is at `path.row.column`. */
  path: string;
  field: FieldBinder;
}) {
  const id = useId();
  const list = field(path);

  const setAt = (i: number, col: 0 | 1, next: string) =>
    onChange(
      value.map((row, r) =>
        r === i ? ((col === 0 ? [next, row[1]] : [row[0], next]) as LineRow) : row,
      ),
    );

  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
        {label}
      </legend>
      {hint && <p className="m-0 mt-2 text-base text-ink-muted">{hint}</p>}

      {value.length > 0 && (
        <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
          {value.map((row, i) => {
            const cells = [field(`${path}.${i}.0`), field(`${path}.${i}.1`)] as const;
            // A row shows one message at a time, the first cell's first.
            const error = cells[0].error ?? cells[1].error;
            const errorId = `${id}-${i}-error`;
            const cellProps = (col: 0 | 1) => ({
              'aria-invalid': cells[col].error ? true : undefined,
              'aria-describedby': cells[col].error ? errorId : undefined,
              onBlur: cells[col].onBlur,
            });
            return (
              <li key={i} className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type={firstType}
                    className={inputCls + ' ' + firstWidth}
                    placeholder={columns[0]}
                    aria-label={`${label} ${i + 1}, ${columns[0]}`}
                    value={row[0]}
                    onChange={(e) => setAt(i, 0, e.target.value)}
                    {...cellProps(0)}
                  />
                  <input
                    className={inputCls + ' min-w-0 flex-1'}
                    placeholder={columns[1]}
                    aria-label={`${label} ${i + 1}, ${columns[1]}`}
                    value={row[1]}
                    onChange={(e) => setAt(i, 1, e.target.value)}
                    {...cellProps(1)}
                  />
                  <Button
                    variant="quiet"
                    aria-label={`Remove ${label} ${i + 1}`}
                    onClick={() => onChange(value.filter((_, r) => r !== i))}
                  >
                    Remove
                  </Button>
                </div>
                {error && <FieldError id={errorId}>{error}</FieldError>}
              </li>
            );
          })}
        </ul>
      )}

      {list.error && (
        <div className="mt-3">
          <FieldError>{list.error}</FieldError>
        </div>
      )}

      <Button
        variant="quiet"
        className="mt-3"
        onClick={() => onChange([...value, ['', '']])}
        // The API takes 30 lines per list.
        disabled={value.length >= 30}
      >
        Add line
      </Button>
    </fieldset>
  );
}
